import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { ForgotPasswordViewPropsType } from './types';
import CustomIcon from '@Assets/images/icons';
import { useIntl } from 'react-intl';
import Api from '@Api/index';
import { ContainerForgot } from './styled';
import { Header, Text } from '@Components/Text';
import { TextBtnSubmit, SubTitle, SubTitleBlue, ErrorText } from '../styled';

const initialValues = {
	email: '',
};

const forgotPasswordSchema = Yup.object().shape({
	email: Yup.string()
		.email('La dirección ingresada no tiene un formato válido de email')
		.required('Debe ingresar un e-mail válido'),
});

const ForgotPasswordView: React.FC<ForgotPasswordViewPropsType> = () => {
	const [status, setStatus] = useState('');
	const [loading, setLoading] = useState(false);
	const formik = useFormik({
		initialValues,
		validationSchema: forgotPasswordSchema,
		onSubmit: async (values) => {
			setLoading(true);
			try {
				const { status } = await Api.Users.sendRecoveryPasswordEmail(
					values.email
				);

				setLoading(false);
				setStatus(status as string);
			} catch (error) {
				setLoading(false);
				setStatus('error');
			}
		},
	});
	const intl = useIntl();

	const handleClickSendMailAgain = () => {
		formik.setFieldValue('email', '');
		formik.setFieldTouched('email', false);
		setStatus('');
	};

	useEffect(() => {
		if (status) {
			setLoading(false);
		}
	}, [status]);

	return status === 'success' ? (
		<ContainerForgot>
			<div>
				<div className="text-center mb-10">
					<div className="div mb-4">
						<CustomIcon icon="checkCircle" />
					</div>
					<Header className=" mb-3 mt-5 pt-5" variant="h1" weight="bold">
						{intl.formatMessage({
							id: 'AUTH.FORGOT.PASSWORD.TITLE',
						})}
					</Header>
					<SubTitle className="mt-5">
						{intl.formatMessage({
							id: 'AUTH.FORGOT.PASSWORD.CHECK.EMAIL',
						})}
					</SubTitle>
				</div>
			</div>
			<div className="d-flex flex-wrap justify-content-center pb-lg-0">
				<Link to="/auth/login">
					<SubTitleBlue>
						{intl.formatMessage({
							id: 'AUTH.REGISTER.REGISTERSENDVIEW.6',
						})}
					</SubTitleBlue>
				</Link>
			</div>
		</ContainerForgot>
	) : status === 'error' ? (
		<ContainerForgot>
			<div className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework">
				<div className="text-center mb-10">
					<div className="div mb-4">
						<CustomIcon icon="alertSignRed" />
					</div>
					<Header className=" mb-3 mt-5 pt-5" variant="h1" weight="bold">
						{intl.formatMessage({
							id: 'AUTH.REGISTER.REGISTERSENDVIEW.3',
						})}
					</Header>
					<SubTitle className="mt-5">
						{intl.formatMessage({
							id: 'AUTH.REGISTER.FORGOTPASS.2',
						})}
					</SubTitle>
				</div>
			</div>
			<div className="d-flex col-12 flex-wrap justify-content-center pb-lg-0 w-100">
				<button
					type="button"
					onClick={handleClickSendMailAgain}
					className="col-12 btn btn-lg btn-primary fw-bolder me-4 mb-3"
				>
					<TextBtnSubmit>
						{intl.formatMessage({
							id: 'AUTH.REGISTER.REGISTERSENDVIEW.5',
						})}
					</TextBtnSubmit>
				</button>
			</div>
			<div className="d-flex flex-wrap justify-content-center pb-lg-0 mt-4">
				<Link to="/auth/login">
					<SubTitleBlue>
						{intl.formatMessage({
							id: 'AUTH.REGISTER.REGISTERSENDVIEW.6',
						})}
					</SubTitleBlue>
				</Link>{' '}
			</div>
		</ContainerForgot>
	) : (
		<ContainerForgot>
			<form
				className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
				noValidate
				id="kt_login_password_reset_form"
				onSubmit={formik.handleSubmit}
			>
				<div className="text-center mb-10">
					{/* begin::Title */}
					<Header className=" mb-3 mt-5 pt-5" variant="h1" weight="bold">
						{intl.formatMessage({ id: 'AUTH.REGISTER.FORGOTPASS.3' })}
					</Header>
					{/* end::Title */}
					{/* begin::Link */}
					<SubTitle className="mt-5">
						{intl.formatMessage({ id: 'AUTH.REGISTER.FORGOTPASS.4' })}
					</SubTitle>
					{/* end::Link */}
				</div>

				{/* begin::Form group */}
				<div className="fv-row mb-10">
					<Text weight="extra-bold">
						{intl.formatMessage({ id: 'AUTH.EMAIL' })}
					</Text>
					<input
						type="email"
						placeholder="mariaclara@e-valuados.com"
						autoComplete="off"
						{...formik.getFieldProps('email')}
						className={clsx(
							'form-control form-control-lg form-control-solid',
							{ 'is-invalid': formik.touched.email && formik.errors.email },
							{
								'is-valid': formik.touched.email && !formik.errors.email,
							}
						)}
					/>
					{formik.touched.email && formik.errors.email && (
						<div className="fv-plugins-message-container">
							<div className="fv-help-block">
								<ErrorText>
									{intl.formatMessage({ id: formik.errors.email })}
								</ErrorText>
							</div>
						</div>
					)}
				</div>
				{/* end::Form group */}

				{/* begin::Form group */}

				<div className="d-flex flex-wrap justify-content-center pb-lg-0  ">
					<button
						type="submit"
						id="kt_password_reset_submit"
						className="col-12 btn btn-lg btn-primary fw-bolder me-4 mb-3"
					>
						{!loading && (
							<TextBtnSubmit>
								{intl.formatMessage({ id: 'BTN.CHANGEPASSWORD' })}
							</TextBtnSubmit>
						)}

						{loading && (
							<span
								className="indicator-progress"
								style={{ display: 'flex', justifyContent: 'center' }}
							>
								<TextBtnSubmit>
									{intl.formatMessage({ id: 'AUTH.REGISTER.STEP1.16' })}{' '}
								</TextBtnSubmit>
								<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
							</span>
						)}
					</button>
				</div>
				<div className="d-flex flex-wrap justify-content-center pb-lg-0">
					<Link to="/auth/login">
						<button
							type="button"
							id="kt_login_password_reset_form_cancel_button"
							className="btn text-primary fw-bolder"
							disabled={formik.isSubmitting || !formik.isValid}
						>
							<SubTitleBlue>
								{intl.formatMessage({ id: 'BTN.LOGIN' })}
							</SubTitleBlue>
						</button>
					</Link>{' '}
				</div>
				{/* end::Form group */}
			</form>
		</ContainerForgot>
	);
};
export default ForgotPasswordView;
